import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Phone, Mail, MessageCircle, Loader2, CheckCircle, MapPin, Clock } from "lucide-react";
import axios from "axios";
import useScrollReveal from "@/hooks/useScrollReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const sectionRef = useScrollReveal();
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", interest: "Erlebniskochen", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const locations = ["Krefeld", "Willich", "Tönisvorst", "Kempen", "Meerbusch", "Viersen", "Moers", "Neukirchen-Vluyn", "Duisburg-West"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: `Interesse: ${formData.interest}${formData.message ? `\n\n${formData.message}` : ""}`
      });
      toast.success("Vielen Dank für deine Anfrage!", { description: "Ich melde mich innerhalb von 24 Stunden bei dir." });
      setIsSubmitted(true);
      setFormData({ name: "", email: "", phone: "", interest: "Erlebniskochen", message: "" });
    } catch (error) {
      toast.error("Etwas ist schiefgelaufen.", { description: "Bitte versuche es erneut oder kontaktiere mich direkt." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappLink = "https://wa.me/491721697433?text=Hallo!%20Ich%20interessiere%20mich%20für%20eine%20Thermomix-Vorführung.";

  return (
    <section id="contact" data-testid="contact-section" className="section-padding relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=60" alt="" className="w-full h-full object-cover opacity-[0.07]" />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-50 via-stone-50/95 to-stone-50" />
      </div>

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">Kontakt</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6 font-[Outfit]">
            Kontakt – Deine Thermomix® Beraterin in Krefeld
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed">
            Egal ob Frage zum TM7, Erlebniskochen oder Karriere – ich freue mich auf deine Nachricht aus Krefeld und Umgebung!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-5xl mx-auto">
          {/* Form + Portrait */}
          <div className="space-y-6 reveal">
            <div className="glass-card rounded-[2rem] p-8 md:p-10">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3 font-[Outfit]">Anfrage erhalten!</h3>
                <p className="text-slate-600 mb-6">Ich melde mich innerhalb von 24 Stunden bei dir.</p>
                <Button onClick={() => setIsSubmitted(false)} className="btn-secondary px-6 py-3">Weitere Nachricht senden</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <h3 className="text-2xl font-bold text-slate-900 font-[Outfit] mb-2">Schnellanfrage</h3>
                <Input type="text" placeholder="Dein Name *" required data-testid="contact-name-input" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="form-input" />
                <Input type="email" placeholder="E-Mail Adresse *" required data-testid="contact-email-input" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="form-input" />
                <Input type="tel" placeholder="Telefon (optional)" data-testid="contact-phone-input" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="form-input" />
                <select value={formData.interest} data-testid="contact-interest-select"
                  onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                  className="form-input appearance-none cursor-pointer">
                  <option value="Erlebniskochen">Interesse an Erlebniskochen</option>
                  <option value="TM7 Kauf">Frage zum TM7 Kauf</option>
                  <option value="Team-Partner">Team-Partner werden (0€ Prog.)</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
                <Button type="submit" data-testid="contact-submit-button" disabled={isSubmitting}
                  className="w-full btn-primary py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Wird gesendet...</>) : (<><Send className="w-5 h-5" />Anfrage senden</>)}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Ich melde mich innerhalb von 24 Stunden. Deine Daten sind gemäß{" "}
                  <a href="/datenschutz" className="text-emerald-600 hover:underline">Datenschutzerklärung</a> sicher.
                </p>
              </form>
            )}
            </div>

            {/* Portrait-Karte – nur Desktop, füllt den freien Platz */}
            <div className="hidden lg:block relative group">
              <div className="absolute -inset-3 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-[2rem] rotate-1 group-hover:rotate-2 transition-transform" />
              <div className="relative glass-card rounded-[2rem] overflow-hidden shadow-xl">
                <div className="relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_elated-ritchie-8/artifacts/2wrchxnl_a6d10632-77a1-4936-a4cb-89e01d811e67.jpeg"
                    alt="Jennifer Laak - Thermomix Beraterin Krefeld - TM7 Beratung im Showroom"
                    data-testid="contact-portrait"
                    loading="lazy"
                    className="w-full aspect-[5/4] object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-7">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 mb-3">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-xs font-bold uppercase tracking-widest text-white">Jetzt erreichbar</span>
                    </div>
                    <div className="text-2xl font-bold text-white font-[Outfit] leading-tight mb-1">
                      Ich freu mich auf deine Nachricht!
                    </div>
                    <div className="text-sm text-white/80">
                      – Jennifer, deine Thermomix®-Beraterin in Krefeld
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className="space-y-6 reveal reveal-delay-1">
            {/* WhatsApp */}
            <div className="glass-card rounded-[2rem] p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-green-500/20">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-[Outfit]">Lieber per WhatsApp?</h3>
              <p className="text-slate-600 mb-6">Schnell und unkompliziert – ich antworte meist innerhalb weniger Stunden.</p>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-button" className="btn-whatsapp w-full px-8 py-5 text-lg">
                <MessageCircle className="w-5 h-5" />WhatsApp öffnen
              </a>
            </div>

            {/* Direct Contact */}
            <div className="glass-card rounded-[2rem] p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6 font-[Outfit]">Direkter Kontakt</h3>
              <div className="space-y-4">
                <a href="tel:+491721697433" data-testid="contact-phone-link" className="flex items-center gap-4 p-4 rounded-2xl bg-white/60 hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-all group">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">Anrufen / WhatsApp</div>
                    <div className="font-semibold text-slate-900">0172 1697433</div>
                  </div>
                </a>
                <a href="mailto:jennifer-laak@gmx.de" data-testid="contact-email-link" className="flex items-center gap-4 p-4 rounded-2xl bg-white/60 hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-all group">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <Mail className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">E-Mail</div>
                    <div className="font-semibold text-slate-900">jennifer-laak@gmx.de</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Öffnungszeiten */}
            <div className="glass-card rounded-[2rem] p-6">
              <div className="flex items-center gap-2 text-emerald-600 font-bold mb-4">
                <Clock className="w-5 h-5" /> Erreichbarkeit
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex justify-between"><span>Montag – Freitag</span><span className="font-semibold text-slate-800">9:00 – 18:00 Uhr</span></div>
                <div className="flex justify-between"><span>Samstag</span><span className="font-semibold text-slate-800">9:00 – 18:00 Uhr</span></div>
                <div className="flex justify-between"><span>Sonntag</span><span className="font-semibold text-slate-500 italic">Schriftliche Anfrage</span></div>
              </div>
            </div>

            {/* Location Radius */}
            <div className="glass-card rounded-[2rem] p-6">
              <div className="flex items-center gap-2 text-emerald-600 font-bold mb-4">
                <MapPin className="w-5 h-5" /> Mein Aktionsradius
              </div>
              <div className="flex flex-wrap gap-2">
                {locations.map(loc => (
                  <span key={loc} className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">{loc}</span>
                ))}
                <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 rounded-full border border-emerald-200">+ 25km</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
